import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Select, MenuItem, TextField, Button, Box, FormControl, InputLabel, Typography, Chip
} from '@mui/material';
import { clarities, colors, sieveSizes } from '../../constants/Constants';
import { apiRequest } from '../../utils/apiClient';
import { useVendorContext } from '../../context/VendorContext';

type Entry = {
  id?: number | null;
  vendor: string;
  color: string;
  clarity: string;
  sieveSize: string;
  price: number | null;
};

export default function DiamondPricingTable() {
  const { vendor } = useVendorContext();
  const [color, setColor] = useState('EF');
  const [clarity, setClarity] = useState('VVS');
  const [priceChart, setPriceChart] = useState<Entry[]>();

  const [data, setData] = useState<Entry[]>(
    sieveSizes.map(size => ({
      sieveSize: size,
      price: null,
      color: '',
      clarity: '',
      vendor: ''
    }))
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!vendor?.vendorName) return;
        const response = await apiRequest(`/lookup/diamond/${vendor.vendorName}`);
        if (response) setPriceChart(response);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [vendor]);

  const handleChangePrice = (index: number, value: any) => {
    setData(prevData => {
      const updated = [...prevData];
      updated[index] = {
        ...updated[index],
        price: Number(value)
      };
      return updated;
    });
  };

  const handleSave = () => {
    const fullData = data.map(entry => ({
      ...entry,
      color,
      clarity,
      vendor: vendor?.vendorName || ''
    }));
    apiRequest(`/lookup/diamond/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: fullData
    });
  };

return (
  <Box sx={{ p: { xs: 1, md: 1.5 }, backgroundColor: '#fafafa', borderRadius: 2 }}>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
        Diamond Pricing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        Select clarity and color once, then fill prices row-wise by sieve size.
      </Typography>
      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        <Chip size="small" label={`Vendor: ${vendor?.vendorName || 'N/A'}`} variant="outlined" />
        <Chip size="small" label={`Rows: ${data.length}`} sx={{ backgroundColor: '#fff7e0' }} />
      </Box>
    </Box>

    {/* Filters Section */}
    <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Clarity</InputLabel>
          <Select
            value={clarity}
            label="Clarity"
            onChange={(e) => setClarity(e.target.value)}
          >
            {clarities.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Color</InputLabel>
          <Select
            value={color}
            label="Color"
            onChange={(e) => setColor(e.target.value)}
          >
            {colors.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Vendor"
          value={vendor?.vendorName || ''}
          disabled
          sx={{ minWidth: 200 }}
        />

        <Button
          variant="contained"
          size="small"
          sx={{ ml: 'auto', textTransform: 'none', fontWeight: 600, backgroundColor: '#d4af37', color: '#000', '&:hover': { backgroundColor: '#c29a2e' } }}
          onClick={handleSave}
        >
          Save All
        </Button>
      </Box>
    </Paper>

    {/* Dual Column Layout */}
    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={1.5}>
      {/* Existing Price Chart */}
      {priceChart && priceChart.length > 0 && (
        <Paper elevation={0} sx={{ flex: 1, p: 1.5, minWidth: 300, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Existing Prices ({priceChart.length})
          </Typography>
          <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.9, px: 1.1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Sieve Size</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Clarity</TableCell>
                <TableCell>Price (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {priceChart.map((entry, idx) => (
                <TableRow key={idx}>
                  <TableCell>{entry.sieveSize}</TableCell>
                  <TableCell>{entry.color}</TableCell>
                  <TableCell>{entry.clarity}</TableCell>
                  <TableCell>₹{entry.price?.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Entry Form Table */}
      <Paper elevation={0} sx={{ flex: 1, p: 1.5, minWidth: 300, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Add / Update Prices
        </Typography>
        <TableContainer>
          <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.8, px: 1.1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Sieve Size</TableCell>
                <TableCell>Price (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={row.sieveSize}>
                  <TableCell>{row.sieveSize}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={row.price ?? ''}
                      onChange={(e) => handleChangePrice(index, e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  </Box>
);

}
